import { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import PublicHeader from '../components/PublicHeader';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { donationsApi } from '../lib/api';
import AppFooter from '../components/AppFooter';

/**
 * Public donation page.
 *
 * Real money moves through GoCardless's own hosted Billing Request Flow —
 * see backend/app/routers/donations.py. This page never touches bank
 * details itself: it creates the billing request, then sends the browser to
 * GoCardless's page (via a brief transitional modal rather than a jarring
 * instant redirect) and back again to /donate/thank-you.
 *
 * GET /api/donations/status reports whether a GoCardless access token is
 * configured in this environment; without one the page falls back to
 * recording intent only, same as before.
 */
const PRESET_AMOUNTS = [5, 10, 25, 50, 100];

// Pessimistic cost breakdown — mirrors the visual cost card on screen and
// keeps all numbers in one place for easy future tuning.
//
// Deliberately priced at full published API rates (no batch/bulk discount
// assumed) and at redundant, always-on infrastructure — not the cheapest
// single-box setup we could get away with. If real costs come in lower
// (they usually do), that's headroom, not a shortfall.
const COST_BREAKDOWN = [
  {
    label: 'AI email classification',
    detail: 'Around 120 emails to and from organisations read and sorted, at standard (non-discounted) API pricing',
    amount: 8.00,
  },
  {
    label: 'AI timeline, redaction and safeguarding analysis',
    detail: 'Around 400 pages of records read by AI vision models at full market rate, including re-runs when a page needs a second pass',
    amount: 22.50,
  },
  {
    label: 'Encrypted document storage',
    detail: 'Around 6GB of records across up to 30 requests, mirrored across separate locations and kept for three years',
    amount: 6.50,
  },
  {
    label: 'A dedicated email address',
    detail: 'A private inbox, monitored around the clock, used for all correspondence with organisations',
    amount: 2.20,
  },
  {
    label: 'Application and database servers',
    detail: 'Multiple high-capacity servers kept running in parallel, so the service stays up and nobody’s records are ever on a single machine',
    amount: 11.00,
  },
  {
    label: 'Document scanning and processing capacity',
    detail: 'Extra server capacity for OCR and preparing scanned pages before AI analysis can read them',
    amount: 5.50,
  },
  {
    label: 'Security monitoring, audits and on-call support',
    detail: 'Round-the-clock monitoring, independent security reviews, and a person to help when something goes wrong',
    amount: 7.30,
  },
];

const TOTAL_COST = COST_BREAKDOWN.reduce((s, c) => s + c.amount, 0);

const DONATE_FAQ = [
  {
    q: 'Is Precious Data a registered charity?',
    a: 'Precious Data is run as a not-for-profit service. Every pound taken in goes toward running the service and reaching more care leavers, never to shareholders or profit.',
  },
  {
    q: 'Will people using the service ever be asked to pay?',
    a: 'No. Requesting your own records, tracking replies, and having them analysed is and will stay free for care leavers. Donations and corporate partnerships are the only way we cover the cost of that.',
  },
  {
    q: 'Is my payment secure, and can I cancel a monthly gift?',
    a: 'Donations are taken through GoCardless, a UK-regulated payment provider used by banks and government services, directly from your bank account rather than a card. You can cancel a monthly donation at any time; nothing is owed for future months.',
  },
  {
    q: 'How do you decide the cost estimates on this page?',
    a: 'Deliberately pessimistically: full published AI and hosting prices, redundant servers rather than the cheapest single box, and three years of storage per person. If the real cost turns out lower, that becomes a safety margin, not a reason to lower the numbers shown here. That figure is running costs only — it does not include paying the team, which is covered separately above.',
  },
  {
    q: 'Can I give in another way?',
    a: 'Yes — bank transfer, a gift in a will, matched giving through your employer, or a corporate partnership are all things we can arrange directly. Email hello@preciousdata.org.uk and we will get back to you.',
  },
];

export const Donate = () => {
  const [amount, setAmount] = useState(25);
  const [customAmount, setCustomAmount] = useState('');
  const [frequency, setFrequency] = useState('one_off');
  const [submitting, setSubmitting] = useState(false);
  const [donationsEnabled, setDonationsEnabled] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [redirectError, setRedirectError] = useState('');

  useEffect(() => {
    donationsApi.status()
      .then(({ data }) => setDonationsEnabled(!!data?.enabled))
      .catch(() => setDonationsEnabled(false))
      .finally(() => setStatusLoaded(true));
  }, []);

  const resolvedAmount = amount === 'custom' ? parseFloat(customAmount) : amount;

  const handleDonate = async () => {
    if (!resolvedAmount || resolvedAmount < 1) {
      toast.error('Choose or enter an amount of £1 or more');
      return;
    }

    if (!donationsEnabled) {
      setSubmitting(true);
      try {
        // Stub — this environment has no GoCardless access token configured yet.
        await new Promise((r) => setTimeout(r, 600));
        toast.success(
          `Thank you. The ${frequency === 'monthly' ? 'monthly' : 'one-off'} £${resolvedAmount.toFixed(2)} payment will go live as soon as GoCardless is set up.`
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    setRedirectError('');
    setRedirecting(true);
    try {
      const { data } = await donationsApi.create({ amount: resolvedAmount, frequency });
      window.location.href = data.authorisation_url;
    } catch (e) {
      setRedirectError(e?.response?.data?.detail || 'Something went wrong starting your donation. Please try again.');
    }
  };

  const segmentClass = (active) =>
    `min-h-[44px] px-4 rounded-full text-sm font-semibold transition-colors duration-state ${
      active ? 'bg-surface text-ink' : 'text-ink-55 hover:text-ink'
    }`;

  const amountClass = (active) =>
    `min-h-[48px] rounded-md font-semibold text-[15px] transition-colors duration-state ${
      active ? 'bg-brand text-white' : 'bg-ground text-ink hover:bg-inset'
    }`;

  return (
    <div className="min-h-screen bg-surface text-ink" data-testid="donate-page">
      <PublicHeader />

      {/* Hero */}
      <section className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-11 lg:px-12">
        <div className="max-w-3xl mx-auto space-y-6">
          <span className="inline-block rounded-full bg-brand-wash text-brand-ink px-4 py-2 text-[15px] font-medium">
            Keep Precious Data free for care leavers
          </span>
          <h1 className="font-heading text-[40px] sm:text-[52px] font-bold tracking-[-0.04em] leading-[0.98]">
            Help a care leaver read their own story.
          </h1>
          <p className="text-[19px] leading-[1.5] text-ink-70">
            Every care leaver has the legal right to see what was written about their childhood.
            Getting it is slow and often obstructed. Precious Data makes it possible, private, and
            free at the point of use. Your donation keeps it that way.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Button
              onClick={() => document.getElementById('donate')?.scrollIntoView({ behavior: 'smooth' })}
              size="lg"
              data-testid="hero-donate-btn"
            >
              Donate now
            </Button>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => document.getElementById('cost-breakdown')?.scrollIntoView({ behavior: 'smooth' })}
              data-testid="hero-costs-btn"
            >
              See where the money goes
            </Button>
          </div>
          <p className="text-sm text-ink-55">
            It costs roughly £{TOTAL_COST.toFixed(0)} to support one care leaver for three years. No
            one using Precious Data is ever asked to pay.
          </p>
        </div>
      </section>

      {/* Why it matters */}
      <section className="px-7 lg:px-12 py-16">
        <div className="max-w-6xl mx-auto grid md:grid-cols-2 gap-12 items-start">
          <div className="space-y-5">
            <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05]">
              Records shape identity.
            </h2>
            <div className="space-y-4 text-ink-70 leading-relaxed">
              <p>
                Care-experienced young people routinely leave the system without knowing why
                decisions were made for them. Who were their birth siblings? Why did one placement
                end? Who was the teacher who believed in them? The answers are often sitting in
                the files of six or seven different organisations, and getting them takes legal
                know-how, a thick skin, and months of chasing.
              </p>
              <p>
                A <strong className="text-ink">Subject Access Request</strong> is a
                statutory right under UK GDPR and the Data Protection Act 2018 (as amended by the
                Data (Use and Access) Act 2025). Precious Data handles the mechanics: drafting the
                letters, tracking the deadlines, encrypted storage, redaction analysis. The care
                leaver can focus on understanding their own life.
              </p>
              <p>
                We will never charge the people who need us. But the service costs money to run.
              </p>
            </div>
          </div>
          <div id="cost-breakdown" className="bg-ground rounded-xl p-6 md:p-8 space-y-5 scroll-mt-6">
            <h3 className="font-heading text-[22px] font-semibold tracking-[-0.02em]">What your donation pays for</h3>
            <p className="text-sm text-ink-55">
              A deliberately pessimistic estimate, per care leaver, assuming a heavy caseload: 30 or
              more requests, 6GB of records, three years of active use. We price this at full,
              published AI and hosting rates, and on infrastructure with built-in redundancy — not the
              cheapest setup we could get away with — so a donation comfortably covers the real cost,
              even in a bad month.
            </p>
            <ul className="space-y-2 pt-2">
              {COST_BREAKDOWN.map((c) => (
                <li
                  key={c.label}
                  className="flex items-start justify-between gap-4 p-4 rounded-lg bg-surface"
                >
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold leading-snug">{c.label}</p>
                    <p className="text-sm text-ink-55 leading-relaxed mt-0.5">{c.detail}</p>
                  </div>
                  <p className="pd-mono text-ink flex-shrink-0 pt-1">£{c.amount.toFixed(2)}</p>
                </li>
              ))}
            </ul>
            <div className="flex items-center justify-between pt-4 border-t border-rule">
              <p className="font-heading font-semibold text-[18px]">
                Total per person, roughly
              </p>
              <p className="font-heading font-bold text-[28px] tracking-[-0.035em]">
                £{TOTAL_COST.toFixed(2)}
              </p>
            </div>
            <p className="text-sm text-ink-55">
              A £2 monthly donation covers one care leaver&apos;s full three years on Precious Data,
              at this deliberately pessimistic estimate. A £10 monthly donation supports several.
            </p>
          </div>
        </div>
      </section>

      {/* This also pays people, not just servers */}
      <section className="px-7 lg:px-12 pb-16">
        <div className="max-w-3xl mx-auto space-y-4">
          <h2 className="font-heading text-[26px] font-semibold tracking-[-0.02em] leading-[1.1]">
            This also pays people, not just servers.
          </h2>
          <p className="text-ink-70 leading-relaxed">
            The figure above only covers what it costs to run the technology — API calls, storage,
            hosting. It does not include paying anyone who builds, maintains or supports Precious Data.
          </p>
          <p className="text-ink-70 leading-relaxed">
            Right now the team is small and largely unpaid. That will not stay true forever: a service
            handling records this sensitive, for people who often have nobody else in their corner,
            needs people who can give it real time, not just volunteers fitting it in around other jobs.
            Being able to pay staff fairly — including anyone on the team who is care-experienced
            themselves — is part of what larger and monthly donations go toward as the service grows.
          </p>
          <p className="text-ink-70 leading-relaxed">
            We will publish real numbers here once we have an actual staffing budget to share. For now,
            know that some of what you give may go toward wages, not only servers.
          </p>
        </div>
      </section>

      {/* Donation form */}
      <section id="donate" className="mx-2.5 mb-2.5 rounded-2xl bg-ground px-7 py-16 lg:px-12">
        <div className="max-w-2xl mx-auto">
          <div className="space-y-3 mb-8">
            <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05]">
              Make a donation
            </h2>
            <p className="text-ink-70">
              Instant Bank Pay through GoCardless. No card fees, straight from your bank.
            </p>
          </div>
          <div className="bg-surface rounded-xl p-6 md:p-8 space-y-8">
            {/* Frequency toggle */}
            <div className="grid grid-cols-2 gap-1 p-1 rounded-full bg-ground" role="group" aria-label="How often">
              <button
                type="button"
                onClick={() => setFrequency('one_off')}
                className={segmentClass(frequency === 'one_off')}
                aria-pressed={frequency === 'one_off'}
                data-testid="freq-oneoff-btn"
              >
                One-off
              </button>
              <button
                type="button"
                onClick={() => setFrequency('monthly')}
                className={segmentClass(frequency === 'monthly')}
                aria-pressed={frequency === 'monthly'}
                data-testid="freq-monthly-btn"
              >
                Monthly
              </button>
            </div>

            {/* Amount pills */}
            <div className="space-y-4">
              <p className="text-[15px] font-semibold">
                {frequency === 'monthly' ? 'How much each month?' : 'How much?'}
              </p>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {PRESET_AMOUNTS.map((a) => (
                  <button
                    type="button"
                    key={a}
                    onClick={() => {
                      setAmount(a);
                      setCustomAmount('');
                    }}
                    className={amountClass(amount === a)}
                    aria-pressed={amount === a}
                    data-testid={`donate-amount-${a}`}
                  >
                    £{a}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setAmount('custom')}
                  className={amountClass(amount === 'custom')}
                  aria-pressed={amount === 'custom'}
                  data-testid="donate-amount-custom"
                >
                  Other
                </button>
              </div>
              {amount === 'custom' && (
                <div className="max-w-xs space-y-2">
                  <Label htmlFor="donate-custom" className="text-[15px] font-semibold block">Enter an amount in pounds</Label>
                  <Input
                    id="donate-custom"
                    type="number"
                    min="1"
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="donate-custom-input"
                  />
                </div>
              )}
            </div>

            {/* CTA */}
            <Button
              onClick={handleDonate}
              disabled={submitting || redirecting || !resolvedAmount || resolvedAmount < 1}
              size="lg"
              className="w-full"
              data-testid="donate-submit-btn"
            >
              {submitting || redirecting
                ? 'Preparing secure checkout…'
                : frequency === 'monthly'
                ? `Donate £${resolvedAmount ? resolvedAmount.toFixed(2) : '0'} monthly`
                : `Donate £${resolvedAmount ? resolvedAmount.toFixed(2) : '0'} once`}
            </Button>

            {statusLoaded && !donationsEnabled && (
              <p className="p-4 rounded-lg bg-amber-wash text-amber-ink text-sm leading-relaxed">
                <strong>Not live yet.</strong> We are finishing our GoCardless setup. For now, clicking
                donate records your intent, and we will be in touch once bank payments are working.
              </p>
            )}

            <p className="text-sm text-ink-55">
              Bank-level security · No card fees · Cancel at any time
            </p>
          </div>
        </div>
      </section>

      {/* Corporate supporters */}
      <section className="px-7 lg:px-12 py-16">
        <div className="max-w-6xl mx-auto space-y-10">
          <div className="space-y-3 max-w-2xl">
            <p className="pd-eyebrow">Corporate supporters</p>
            <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05]">
              Partner with us.
            </h2>
            <p className="text-ink-70 leading-relaxed">
              Organisations who believe care leavers deserve a fair chance at their own records. We
              list supporters here and report every year on exactly how their money was spent.
            </p>
          </div>

          {/* Placeholder partner grid — 6 slots ready to drop real logos into */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="partner-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-28 rounded-lg bg-inset flex items-center justify-center text-ink-55 text-sm"
                data-testid={`partner-slot-${i}`}
              >
                Your logo here
              </div>
            ))}
          </div>

          {/* Partner CTA */}
          <div className="bg-ground rounded-xl p-8 md:p-10 grid md:grid-cols-[1fr_auto] items-center gap-6">
            <div className="space-y-2">
              <h3 className="font-heading text-[24px] font-semibold tracking-[-0.02em]">
                Become a corporate partner
              </h3>
              <p className="text-ink-70 leading-relaxed">
                Sponsor a group of care leavers for a year, fund a specific capability, or
                underwrite our infrastructure. We will work out a package that fits.
              </p>
            </div>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => (window.location.href = 'mailto:partners@preciousdata.org.uk?subject=Corporate%20partnership%20enquiry')}
              data-testid="partner-contact-btn"
            >
              Get in touch
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="px-7 lg:px-12 py-16">
        <div className="max-w-3xl mx-auto space-y-8">
          <div className="space-y-3">
            <p className="pd-eyebrow">Questions</p>
            <h2 className="font-heading text-[34px] font-bold tracking-[-0.035em] leading-[1.05]">
              Before you donate.
            </h2>
          </div>
          <dl className="space-y-6">
            {DONATE_FAQ.map((item) => (
              <div key={item.q} className="space-y-1.5">
                <dt className="text-[17px] font-semibold text-ink">{item.q}</dt>
                <dd className="text-ink-70 leading-relaxed">{item.a}</dd>
              </div>
            ))}
          </dl>
          <p className="text-sm text-ink-55 pt-2 border-t border-rule">
            Prefer to give another way, or have a question we haven&apos;t answered? Email{' '}
            <a href="mailto:hello@preciousdata.org.uk" className="text-brand hover:underline">
              hello@preciousdata.org.uk
            </a>
            .
          </p>
        </div>
      </section>

      <AppFooter />

      {/* Transitional modal — brief, then a full redirect to GoCardless's own
          hosted, secure payment page (never embedded: bank/payment pages
          block being framed, and GoCardless — not us — needs to see the
          payer's details). Closing this without an error just cancels. */}
      <Dialog open={redirecting} onOpenChange={(open) => { if (!open) { setRedirecting(false); setRedirectError(''); } }}>
        <DialogContent className="max-w-sm text-center" data-testid="donate-redirect-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-[20px] font-semibold tracking-[-0.02em] text-center">
              {redirectError ? 'Could not start your donation' : 'Taking you to GoCardless'}
            </DialogTitle>
          </DialogHeader>
          {redirectError ? (
            <>
              <p className="text-sm text-ink-70">{redirectError}</p>
              <DialogFooter className="sm:justify-center">
                <Button onClick={() => { setRedirecting(false); setRedirectError(''); }} data-testid="donate-redirect-close">
                  Close
                </Button>
              </DialogFooter>
            </>
          ) : (
            <p className="text-sm text-ink-70 flex items-center justify-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
              Setting up your secure {frequency === 'monthly' ? 'Direct Debit' : 'payment'} with GoCardless…
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Donate;
